import React from 'react'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'
import { Spinner } from '@wartek-id/spinner'
import { historyUtils } from '@wartek-id/toolbox-helper'

import {
  useMutateAPIKSSelfProposal,
  useQueryAPIGetKSSelectionCandidate,
} from 'api/bcks/useAPIBCKSInvitation'
import { getDayToDeadlineState } from '../utils/date'
import Header from 'app/BCKSPage/components/Header'
import AndroidHandler from 'utils/androidHandler'
import { SelectionStepsV2 } from '../components/SelectionSteps/SelectionStepsV2'
import { SELECTION_STATES_FINAL_PAGE } from '../constants'
import { useBCKSDesktopView } from 'app/BCKSPage/hooks/useBCKSDesktopView'
import { BCKSDesktopPageShell } from 'app/BCKSPage/components/BCKSDesktopPageShell'
import { BCKSProposalConfirmation } from 'app/BCKSPage/components/BCKSProposalConfirmation'

export const SelfProposalPage = () => {
  const toast = useToast()
  const router = useRouter()

  const [sheetOpen, setSheetOpen] = React.useState(false)

  // --------------------------------------
  // Get candidate data and its derivations
  // --------------------------------------
  const { data: candidateData, isLoading } = useQueryAPIGetKSSelectionCandidate(
    {
      useErrorBoundary: true, // Prevent infinite retry upon failure
    }
  )
  const selectionState = candidateData?.selectionCandidateState
  const deadlineState = getDayToDeadlineState(candidateData?.deadlineDate)
  const isOverdue = deadlineState === 'exceedDeadline'
  const selectionId = candidateData?.selectionId

  // -----------------
  // Handle submission
  // -----------------
  const selfProposalMutation = useMutateAPIKSSelfProposal(selectionId)
  const handleSubmission = () => {
    // Do nothing if there is any current submission
    if (selfProposalMutation.isLoading) return

    selfProposalMutation.mutate(selectionId, {
      onSuccess: () => {
        router.replace('/bcks/invitation/precollection')
      },
      onError: () => {
        // Dismiss the sheet
        setSheetOpen(false)

        // Display error message
        toast({
          message: 'Gagal mengusulkan diri',
        })
      },
    })
  }

  // ---------------------------------
  // Handle navigation and redirection
  // ---------------------------------
  const androidHandler = AndroidHandler()
  const handleClickBack = () => {
    if (androidHandler.enabled) historyUtils.backToApp(router.back)
    else router.back()
  }
  React.useEffect(() => {
    if (!!candidateData) {
      // Redirect to final page
      if (SELECTION_STATES_FINAL_PAGE.includes(selectionState)) {
        router.replace('/bcks/invitation/final')
        return
      }
      if (!isOverdue && selectionState === 'documentSubmission') {
        router.replace('/bcks/invitation/precollection')
        return
      }
      // Otherwise, redirect to homepage
      if (
        selectionState !== 'notInvitedGuru' &&
        selectionState !== 'notInvited'
      ) {
        handleClickBack()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateData, selectionState, deadlineState])

  const { isDesktop } = useBCKSDesktopView()

  if (isDesktop && isLoading) {
    return (
      <BCKSDesktopPageShell title="Pengusulan Diri" pageLabel="Pengusulan Diri">
        <div className="flex flex-col items-center justify-center py-16">
          <Spinner size="lg" />
          <Text className="mt-4" variant="body">
            Halaman sedang dimuat...
          </Text>
        </div>
      </BCKSDesktopPageShell>
    )
  }

  if (isDesktop) {
    return (
      <BCKSDesktopPageShell title="Pengusulan Diri" pageLabel="Pengusulan Diri">
        <div>
          {isOverdue && (
            <div className="bg-surface-selected-default p-4">
              <Text variant="body-sm">
                Maaf, waktu Pengumpulan Berkas sudah lewat sehingga Anda tidak
                bisa mengikuti seleksi di periode ini.
              </Text>
            </div>
          )}
          <div className="p-4">
            <Text>
              Anda dapat mengusulkan diri untuk mengikuti Seleksi BCKS sesuai
              dengan waktu yang ditentukan.
              <br />
              <br />
              Jika Anda ingin mengikuti seleksi BCKS, silakan tekan tombol
              "Usulkan diri" di bawah untuk mulai mengunggah berkas Seleksi
              Administrasi.
            </Text>

            <Text variant="heading-sm" className="mt-6">
              Berikut seluruh tahapan yang akan dilalui jika Anda mengusulkan
              diri untuk ikut seleksi:
            </Text>
          </div>

          <div>
            <SelectionStepsV2 staticView />
          </div>

          <div className="px-4 py-6">
            <Text variant="heading-sm">Penting diketahui:</Text>

            <Text className="mt-1">
              Setelah Anda menekan tombol "Usulkan diri", Anda akan masuk ke
              dalam daftar Seleksi Administrasi dan diharapkan untuk mengunggah
              berkas.
            </Text>

            <Button
              fullWidth
              size="lg"
              className="mt-6"
              disabled={isOverdue}
              onClick={() => {
                setSheetOpen(true)
              }}
            >
              Usulkan diri
            </Button>
          </div>
        </div>

        <BCKSProposalConfirmation
          isOpen={sheetOpen}
          isLoading={selfProposalMutation.isLoading}
          onClose={() => setSheetOpen(false)}
          onConfirm={handleSubmission}
          title="Konfirmasi pengusulan diri"
          description={
            <>
              Dengan memilih <strong>Saya setuju</strong>, berarti Anda akan
              mengikuti seluruh tahapan seleksi Kepala Sekolah dan bersedia
              menerima penempatan yang diputuskan oleh Dinas.
            </>
          }
        />
      </BCKSDesktopPageShell>
    )
  }

  if (isLoading) {
    return (
      <div className="relative flex flex-col h-screen max-w-xl mx-auto items-center">
        <Header
          isSticky
          title="Pengusulan Diri"
          variant="large"
          withRightActionButton={false}
          onBackClick={handleClickBack}
        />
        <main className="flex flex-col content-around sticky w-full h-full p-2 items-center justify-center">
          <Spinner size="lg" />
          <Text className="mt-4" variant="body">
            Halaman sedang dimuat...
          </Text>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header
        isSticky
        title="Pengusulan Diri"
        variant="large"
        withRightActionButton={false}
        onBackClick={handleClickBack}
      />
      <main className="sticky">
        {isOverdue && (
          <div className="bg-surface-selected-default p-4">
            <Text variant="body-sm">
              Maaf, waktu Pengumpulan Berkas sudah lewat sehingga Anda tidak
              bisa mengikuti seleksi di periode ini.
            </Text>
          </div>
        )}
        <div className="p-4">
          <Text>
            Anda dapat mengusulkan diri untuk mengikuti Seleksi BCKS sesuai
            dengan waktu yang ditentukan.
            <br />
            <br />
            Jika Anda ingin mengikuti seleksi BCKS, silakan tekan tombol
            “Usulkan diri” di bawah untuk mulai mengunggah berkas Seleksi
            Administrasi.
          </Text>

          <Text variant="heading-sm" className="mt-6">
            Berikut seluruh tahapan yang akan dilalui jika Anda mengusulkan diri
            untuk ikut seleksi:
          </Text>
        </div>

        <div>
          <SelectionStepsV2 staticView />
        </div>
      </main>
      <footer className="px-4 py-6">
        <Text variant="heading-sm">Penting diketahui:</Text>

        <Text className="mt-1">
          Setelah Anda menekan tombol “Usulkan diri”, Anda akan masuk ke dalam
          daftar Seleksi Administrasi dan diharapkan untuk mengunggah berkas.
        </Text>

        <Button
          fullWidth
          size="lg"
          className="mt-6"
          disabled={isOverdue}
          onClick={() => {
            setSheetOpen(true)
          }}
        >
          Usulkan diri
        </Button>
      </footer>

      <BCKSProposalConfirmation
        isOpen={sheetOpen}
        isLoading={selfProposalMutation.isLoading}
        onClose={() => setSheetOpen(false)}
        onConfirm={handleSubmission}
        title="Konfirmasi pengusulan diri"
        description={
          <>
            Dengan memilih <strong>Saya setuju</strong>, berarti Anda akan
            mengikuti seluruh tahapan seleksi Kepala Sekolah dan bersedia
            menerima penempatan yang diputuskan oleh Dinas.
          </>
        }
      />
    </div>
  )
}
